/**
 * TravelShowcase — Premium renderer for travel / itinerary / trip layouts.
 * Palette: teal primary + activity-type accents.
 * Phase 8 — Athena Presentation Design System.
 */
import React, { useState } from 'react';
import { usePresentationBreakpoint, AthenaTip, ActionChips, SectionCard, HeroPhotoPanel, MetricStrip }
  from '../presentation-system';
import { base } from '../presentation-system/tokens/colors';
import { ProseText } from './ProseText';

const C = {
  accent:        '#2dd4bf',
  accentFade:    'rgba(45,212,191,0.10)',
  accentBorder:  'rgba(45,212,191,0.22)',
  card:          base.card,
  border:        base.border,
  text:          base.text,
  textMuted:     base.textMuted,
  textFaint:     base.textFaint,
};

const ACT_COLORS = {
  transport:     { color: '#60a5fa', fade: 'rgba(96,165,250,0.10)',  border: 'rgba(96,165,250,0.22)',  icon: '✈️' },
  accommodation: { color: '#c084fc', fade: 'rgba(192,132,252,0.10)', border: 'rgba(192,132,252,0.22)', icon: '🏨' },
  food:          { color: '#fb923c', fade: 'rgba(251,146,60,0.10)',  border: 'rgba(251,146,60,0.22)',  icon: '🍽️' },
  attraction:    { color: '#2dd4bf', fade: 'rgba(45,212,191,0.10)',  border: 'rgba(45,212,191,0.22)',  icon: '🗺️' },
  activity:      { color: '#34d399', fade: 'rgba(52,211,153,0.10)',  border: 'rgba(52,211,153,0.22)',  icon: '🎯' },
};

const _card = {
  background: C.card,
  border: `1px solid ${C.border}`,
  borderRadius: 14,
  boxShadow: '0 2px 16px rgba(0,0,0,0.2)',
};

function HeroDesktop({ d }) {
  const titleFs = (d.title || '').length > 38 ? 21 : (d.title || '').length > 24 ? 26 : 30;
  const METRICS = [
    { icon: '📍', value: d.destination || 'Destino',  label: 'Destino'  },
    { icon: '⏱', value: d.duration   || 'Planejado', label: 'Duração'  },
    { icon: '📅', value: d.period     || 'Programado', label: 'Período'  },
    d.budget && { icon: '💰', value: d.budget, label: 'Orçamento' },
  ].filter(Boolean);

  return (
    <div style={{ ..._card, padding: 0, marginBottom: 14, overflow: 'hidden', display: 'flex', height: 260,
      background: 'linear-gradient(135deg, #0b1524 0%, #0d1a2e 100%)',
      boxShadow: '0 4px 40px rgba(0,0,0,0.4), 0 1px 0 rgba(255,255,255,0.03) inset',
    }}>
      <HeroPhotoPanel
        heroImage={d.hero_image}
        badge="✈️ VIAGEM"
        accentColor={C.accent} accentFade={C.accentFade} accentBdr={C.accentBorder}
        placeholder="✈️" placeholderLabel="Viagem"
        title={d.title}
      />
      <div style={{ flex: 1, padding: '20px 24px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minWidth: 0 }}>
        <div>
          <h1 style={{ margin: '0 0 8px', fontSize: titleFs, fontWeight: 800, color: C.text, lineHeight: 1.2, letterSpacing: '-.02em',
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>{d.title}</h1>
          {d.subtitle && <p style={{ margin: '0 0 8px', fontSize: 13, color: base.textSec, lineHeight: 1.5,
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>{d.subtitle}</p>}
          {d.destination && <div style={{ fontSize: 13, color: C.accent, fontWeight: 600 }}>📍 {d.destination}</div>}
        </div>
        <MetricStrip metrics={METRICS.slice(0, 4)} accentColor={C.accent} />
      </div>
    </div>
  );
}

function HeroMobile({ d }) {
  const METRICS = [
    { icon: '📍', value: d.destination || 'Destino',   label: 'Destino'  },
    { icon: '⏱', value: d.duration    || 'Planejado',  label: 'Duração'  },
    { icon: '📅', value: d.period      || 'Programado', label: 'Período'  },
    { icon: '💰', value: d.budget      || 'A definir',  label: 'Orçamento' },
  ];

  return (
    <div style={{ ..._card, padding: 0, marginBottom: 12, overflow: 'hidden',
      background: 'linear-gradient(135deg, #0b1524 0%, #0d1a2e 100%)',
      boxShadow: '0 4px 40px rgba(0,0,0,0.4)',
    }}>
      <div style={{ display: 'flex', minHeight: 202 }}>
        <div style={{ flex: 1, minWidth: 0, padding: '20px 10px 18px 18px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
          <span style={{ display: 'inline-block', alignSelf: 'flex-start', fontSize: 10, fontWeight: 800, letterSpacing: '.09em', textTransform: 'uppercase', color: C.accent, background: C.accentFade, border: `1px solid ${C.accentBorder}`, borderRadius: 5, padding: '2px 8px', marginBottom: 10 }}>✈️ VIAGEM</span>
          {d.destination && <div style={{ fontSize: 12, color: C.accent, fontWeight: 600, marginBottom: 5 }}>📍 {d.destination}</div>}
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: C.text, lineHeight: 1.25 }}>{d.title}</h1>
          {d.subtitle && <p style={{ margin: '6px 0 0', fontSize: 12, color: base.textSec, lineHeight: 1.4,
            display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>{d.subtitle}</p>}
        </div>
        <HeroPhotoPanel
          heroImage={d.hero_image} isMobile
          accentColor={C.accent} accentFade={C.accentFade} accentBdr={C.accentBorder}
          placeholder="✈️"
          title={d.title}
        />
      </div>
      <div style={{ borderTop: `1px solid ${C.border}`, background: 'rgba(0,0,0,0.18)' }}>
        <MetricStrip metrics={METRICS} bordered accentColor={C.accent} />
      </div>
    </div>
  );
}

function ActivityCard({ act, compact }) {
  const ac = ACT_COLORS[act.type] || ACT_COLORS.activity;
  return (
    <div style={{
      display: 'flex', gap: 10,
      padding: compact ? '8px 10px' : '9px 12px',
      background: ac.fade, border: `1px solid ${ac.border}`,
      borderLeft: `3px solid ${ac.color}`, borderRadius: 8, marginBottom: 6,
      alignItems: 'flex-start',
    }}>
      <span style={{ fontSize: compact ? 13 : 15, flexShrink: 0, marginTop: 1 }}>{ac.icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'baseline' }}>
          {act.time && (
            <span style={{ fontSize: 11, fontWeight: 700, color: ac.color, fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>{act.time}</span>
          )}
          <span style={{ fontSize: compact ? 13 : 14, fontWeight: 600, color: C.text }}>{act.title}</span>
          {act.cost && (
            <span style={{ fontSize: 11, color: C.textFaint, marginLeft: 'auto' }}>{act.cost}</span>
          )}
        </div>
        {act.location && <div style={{ fontSize: 11, color: C.textFaint, marginTop: 3 }}>📍 {act.location}</div>}
        {act.description && <div style={{ fontSize: 12, color: C.textMuted, marginTop: 3, lineHeight: 1.5 }}>{act.description}</div>}
      </div>
    </div>
  );
}

function DayBlock({ day, compact }) {
  const [open, setOpen] = useState(true);
  const acts = day.activities || [];
  return (
    <div style={{ ..._card, marginBottom: 10, overflow: 'hidden' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', background: 'none', border: 'none', cursor: 'pointer',
          padding: '10px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          color: C.text,
        }}
      >
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.07em', textTransform: 'uppercase', color: C.accent }}>{day.day}</span>
          {day.date && day.date !== day.day && (
            <span style={{ fontSize: 11, color: C.textFaint }}>{day.date}</span>
          )}
          {day.title && (
            <span style={{ fontSize: 12, color: C.textMuted }}>— {day.title}</span>
          )}
          <span style={{ fontSize: 11, color: C.textFaint, background: 'rgba(148,163,184,0.1)', borderRadius: 10, padding: '1px 7px' }}>
            {acts.length} {acts.length === 1 ? 'atividade' : 'atividades'}
          </span>
        </div>
        <span style={{ fontSize: 12, color: C.textFaint }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div style={{ padding: '0 12px 12px' }}>
          {acts.length > 0
            ? acts.map((act, i) => <ActivityCard key={i} act={act} compact={compact} />)
            : <p style={{ margin: 0, fontSize: 13, color: C.textFaint, fontStyle: 'italic' }}>Dia livre</p>
          }
        </div>
      )}
    </div>
  );
}

function ChecklistSection({ checklist, isMobile }) {
  if (!checklist || checklist.length === 0) return null;
  return (
    <SectionCard title="Checklist de Viagem" borderColor={C.accentBorder} style={{ marginBottom: 12 }}>
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: 10 }}>
        {checklist.map((group, i) => (
          <div key={i}>
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.07em', textTransform: 'uppercase', color: C.accent, marginBottom: 6 }}>
              {group.category}
            </div>
            <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 4 }}>
              {(group.items || []).map((it, j) => (
                <li key={j} style={{ display: 'flex', gap: 7, fontSize: 13, color: C.text }}>
                  <span style={{ color: C.accent, flexShrink: 0, marginTop: 3 }}>•</span>
                  <span style={{ lineHeight: 1.5 }}>{it}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </SectionCard>
  );
}

export default function TravelShowcase({ data, wp, onAction }) {
  const isMobile = usePresentationBreakpoint();
  const d = wp || data;
  if (!d) return null;

  const days      = d.days      || [];
  const checklist = d.checklist || [];
  const documents = d.documents || [];

  return (
    <div style={{ color: C.text, fontFamily: 'inherit', lineHeight: 1.6, maxWidth: '100%' }}>
      {isMobile ? <HeroMobile d={d} /> : <HeroDesktop d={d} />}

      {d.context && !d.subtitle && (
        <div style={{ ..._card, padding: '12px 14px', marginBottom: 12, fontSize: 14, color: C.textMuted, lineHeight: 1.6 }}>
          {d.context}
        </div>
      )}

      {days.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase', color: C.textMuted, marginBottom: 10 }}>
            Roteiro — {days.length} {days.length === 1 ? 'dia' : 'dias'}
          </div>
          {days.map((day, i) => <DayBlock key={i} day={day} compact={isMobile} />)}
        </div>
      )}

      <ChecklistSection checklist={checklist} isMobile={isMobile} />

      {documents.length > 0 && (
        <SectionCard title="Documentos Necessários" borderColor={C.border} style={{ marginBottom: 10 }}>
          <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 5 }}>
            {documents.map((doc, i) => (
              <li key={i} style={{ display: 'flex', gap: 8, fontSize: 13, color: C.text }}>
                <span style={{ color: '#fbbf24', flexShrink: 0 }}>📄</span>
                <span style={{ lineHeight: 1.5 }}>{doc}</span>
              </li>
            ))}
          </ul>
        </SectionCard>
      )}

      {d.budget_detail && (
        <SectionCard title="Orçamento da Viagem" borderColor={C.border} style={{ marginBottom: 10 }}>
          <ProseText text={d.budget_detail} style={{ margin: 0, fontSize: 14, color: C.textMuted, lineHeight: 1.6, whiteSpace: 'pre-wrap' }} />
        </SectionCard>
      )}

      {(d.sections || []).map((s, i) => (
        <SectionCard key={i} title={s.title} borderColor={C.border} style={{ marginBottom: 10 }}>
          <ProseText text={s.content} style={{ margin: 0, fontSize: 14, color: C.textMuted, lineHeight: 1.6, whiteSpace: 'pre-wrap' }} />
        </SectionCard>
      ))}

      <AthenaTip tip={d.tip} accentColor={C.accent} style={{ marginBottom: 14 }} />
      <ActionChips chips={d.follow_up_chips} onAction={onAction} accentColor={C.accent} />
    </div>
  );
}
